/**
 * «Эта папка недели — текущая?» — предикат для визуальной подсветки Диска.
 *
 * Зачем парсить имя, а не брать данные недели: декоратор Диска
 * (`defineDriveDecorations`) СИНХРОННЫЙ — он зовётся на каждый рендер узла
 * дерева и строки списка, запросов делать не может. Всё, что у него есть, —
 * поля папки. Имя папки недели производно от дат недели и держится системой
 * (`is_system`, синхронизация `weekFolderRules`), поэтому оно — надёжный
 * источник: юзер его не переименует.
 *
 * Сравниваем ДАТУ (понедельник из имени), а не номер недели. Номер в имени
 * календарный (ISO), и сверять его с `isoWeekOfYear(сегодня)` — значит тащить
 * капкан границы года: 29–31 декабря по ISO уже 1-я неделя следующего года, а
 * 1–3 января — 52/53-я предыдущего. Дата таких сюрпризов не создаёт.
 *
 * Известное ограничение: год в имени не указан, поэтому «понедельник 14.09»
 * совпал бы у недель разных лет, где 14 сентября — тоже понедельник (повтор
 * через 5–6 лет). Внутри одного проекта верфи (месяцы, максимум год-два) это
 * недостижимо; ради него тащить год в имя папки не стоит.
 */
import { isoWeekStart } from './weekNumber';
import { shipyardTodayIso } from './timezone';

/** Диапазон из имени папки недели, день и месяц (год в имени не хранится). */
export interface WeekFolderRange {
  fromDay: number;
  fromMonth: number;
  toDay: number;
  toMonth: number;
}

/**
 * Разбирает «Неделя 38 (14.09–20.09)» → границы диапазона; `null` — имя не
 * похоже на папку недели.
 *
 * Разделитель принимаем любой из «–» (en dash, его ставит `weekFolderTitle`),
 * «—» и обычного дефиса: имена недель поколений постарше могли прийти из
 * ручного переименования, а падать на них незачем — просто не подсветим.
 */
export function parseWeekFolderRange(name: string): WeekFolderRange | null {
  const match = /\((\d{1,2})\.(\d{1,2})\s*[–—-]\s*(\d{1,2})\.(\d{1,2})\)/.exec(name);
  if (!match) return null;
  const [, fromDay, fromMonth, toDay, toMonth] = match;
  return {
    fromDay: Number(fromDay),
    fromMonth: Number(fromMonth),
    toDay: Number(toDay),
    toMonth: Number(toMonth),
  };
}

/**
 * Текущая ли это неделя.
 *
 * Сравниваем понедельник недели из имени с понедельником сегодняшней ISO-недели
 * (`isoWeekStart`): недели в системе строго пн→вс, поэтому понедельник
 * однозначно определяет неделю, и проверять попадание «сегодня» в диапазон
 * отдельно не нужно.
 *
 * «Сегодня» берётся в КАЛЕНДАРЕ ВЕРФИ (`shipyardTodayIso`), а не в зоне
 * браузера: недели заводит сервер по той же зоне, и на локальной дате
 * пользователя в 19:00 воскресенья по МСК (на верфи уже понедельник) Диск
 * подсвечивал бы прошлую неделю, пока система считает текущей новую.
 *
 * @param name имя папки недели («Неделя 38 (14.09–20.09)»)
 * @param now момент времени — параметром, чтобы предикат оставался чистым и
 *   тестировался без подмены системного времени
 */
export function isCurrentWeekFolderName(name: string, now: Date = new Date()): boolean {
  const range = parseWeekFolderRange(name);
  if (!range) return false;

  const monday = isoWeekStart(shipyardTodayIso(now));
  const [, month, day] = monday.split('-').map(Number);
  return range.fromDay === day && range.fromMonth === month;
}
